import { lazy, Suspense } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { AppShell } from "@/components/layout/AppShell";
import { ProtectedRoute, PublicOnlyRoute } from "./ProtectedRoute";
import { RoleRoute } from "./RoleRoute";

// Route-level code splitting. Nobody should download the org chart to look at
// a payslip.
const LoginPage = lazy(() => import("@/features/auth/pages/LoginPage"));
const SignupPage = lazy(() => import("@/features/auth/pages/SignupPage"));
const ForgotPasswordPage = lazy(() => import("@/features/auth/pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("@/features/auth/pages/ResetPasswordPage"));
const VerifyEmailPage = lazy(() => import("@/features/auth/pages/VerifyEmailPage"));

const DashboardPage = lazy(() => import("@/features/dashboard/pages/DashboardPage"));
const EmployeeListPage = lazy(() => import("@/features/employees/pages/EmployeeListPage"));
const EmployeeProfilePage = lazy(() => import("@/features/employees/pages/EmployeeProfilePage"));
const EmployeeFormPage = lazy(() => import("@/features/employees/pages/EmployeeFormPage"));
const DirectoryPage = lazy(() => import("@/features/employees/pages/DirectoryPage"));
const OrgChartPage = lazy(() => import("@/features/employees/pages/OrgChartPage"));
const CelebrateBirthdayPage = lazy(() => import("@/features/employees/pages/CelebrateBirthdayPage"));
const ProfilePage = lazy(() => import("@/features/settings/pages/ProfilePage"));
const NotificationsPage = lazy(() => import("@/features/notifications/pages/NotificationsPage"));
const AnnouncementsPage = lazy(() => import("@/features/announcements/pages/AnnouncementsPage"));
const MyClaimsPage = lazy(() => import("@/features/expenses/pages/MyClaimsPage"));
const ClaimFormPage = lazy(() => import("@/features/expenses/pages/ClaimFormPage"));
const ClaimDetailPage = lazy(() => import("@/features/expenses/pages/ClaimDetailPage"));
const ApprovalsQueuePage = lazy(() => import("@/features/expenses/pages/ApprovalsQueuePage"));
const AllClaimsPage = lazy(() => import("@/features/expenses/pages/AllClaimsPage"));
const ExpenseCategoriesPage = lazy(() => import("@/features/expenses/pages/ExpenseCategoriesPage"));
const FundsPage = lazy(() => import("@/features/funds/pages/FundsPage"));
const FundDetailPage = lazy(() => import("@/features/funds/pages/FundDetailPage"));
const SecurityPage = lazy(() => import("@/features/settings/pages/SecurityPage"));
const OrganisationPage = lazy(() => import("@/features/settings/pages/OrganisationPage"));
const JobBoardApisPage = lazy(() => import("@/features/settings/pages/JobBoardApisPage"));
const RecruitmentSettingsPage = lazy(() => import("@/features/settings/pages/RecruitmentSettingsPage"));
const AuditLogsPage = lazy(() => import("@/features/settings/pages/AuditLogsPage"));
const ProductivityRulesPage = lazy(() => import("@/features/settings/pages/ProductivityRulesPage"));
const OfferLetterSignaturesPage = lazy(() => import("@/features/recruitment/pages/OfferLetterSignaturesPage"));
const OfferLetterTemplatePage = lazy(() => import("@/features/recruitment/pages/OfferLetterTemplatePage"));
const EmailSettingsPage = lazy(() => import("@/features/settings/pages/EmailSettingsPage"));

const JobPostingsPage = lazy(() => import("@/features/recruitment/pages/JobPostingsPage"));
const CandidatesListPage = lazy(() => import("@/features/recruitment/pages/CandidatesListPage"));
const PipelineBoardPage = lazy(() => import("@/features/recruitment/pages/PipelineBoardPage"));
const JobEditPage = lazy(() => import("@/features/recruitment/pages/JobEditPage"));
const ApplicationDetailPage = lazy(() => import("@/features/recruitment/pages/ApplicationDetailPage"));
const MyPanelPage = lazy(() => import("@/features/recruitment/pages/MyPanelPage"));
const RequestReschedulePage = lazy(() => import("@/features/recruitment/pages/RequestReschedulePage"));
const AcceptReschedulePage = lazy(() => import("@/features/recruitment/pages/AcceptReschedulePage"));
const ConsentFormPage = lazy(() => import("@/features/recruitment/pages/ConsentFormPage"));
const CandidatePortalPage = lazy(() => import("@/features/recruitment/pages/CandidatePortalPage"));

const OnboardingListPage = lazy(() => import("@/features/onboarding/pages/OnboardingListPage"));
const OnboardingDetailPage = lazy(() => import("@/features/onboarding/pages/OnboardingDetailPage"));
const OnboardingPortalPage = lazy(() => import("@/features/onboarding/pages/OnboardingPortalPage"));

const CareersListPage = lazy(() => import("@/features/careers/pages/CareersListPage"));
const CareersDetailPage = lazy(() => import("@/features/careers/pages/CareersDetailPage"));
const OfferAcceptPage = lazy(() => import("@/features/offers/pages/OfferAcceptPage"));
const OffersListPage = lazy(() => import("@/features/offers/pages/OffersListPage"));
const AssetSelfRegisterPage = lazy(() => import("@/features/assets/pages/AssetSelfRegisterPage"));

const DocumentVerificationQueuePage = lazy(() => import("@/features/documents/pages/DocumentVerificationQueuePage"));
const AssetsPage = lazy(() => import("@/features/assets/pages/AssetsPage"));
const LeavePage = lazy(() => import("@/features/leave/pages/LeavePage"));
const HolidaysPage = lazy(() => import("@/features/leave/pages/HolidaysPage"));
const AttendancePage = lazy(() => import("@/features/attendance/pages/AttendancePage"));
const TeamActivityPage = lazy(() => import("@/features/attendance/pages/TeamActivityPage"));
const ActivityAdjustmentPage = lazy(() => import("@/features/attendance/pages/ActivityAdjustmentPage"));
const PayrollPage = lazy(() => import("@/features/payroll/pages/PayrollPage"));
const PerformancePage = lazy(() => import("@/features/performance/pages/PerformancePage"));

const LifecycleOverviewPage = lazy(() => import("@/features/lifecycle/pages/LifecycleOverviewPage"));
const ProbationPage = lazy(() => import("@/features/lifecycle/pages/ProbationPage"));
const InternsPage = lazy(() => import("@/features/lifecycle/pages/InternsPage"));
const ResignationsPage = lazy(() => import("@/features/lifecycle/pages/ResignationsPage"));
const MyResignationPage = lazy(() => import("@/features/lifecycle/pages/MyResignationPage"));
const LettersPage = lazy(() => import("@/features/lifecycle/pages/LettersPage"));
const LetterTemplatesPage = lazy(() => import("@/features/lifecycle/pages/LetterTemplatesPage"));
const ProfileChangesPage = lazy(() => import("@/features/lifecycle/pages/ProfileChangesPage"));
const PartTimeEmployeesPage = lazy(() => import("@/features/lifecycle/pages/PartTimeEmployeesPage"));
const FreelancersPage = lazy(() => import("@/features/lifecycle/pages/FreelancersPage"));
const FreelancerPaymentsPage = lazy(() => import("@/features/lifecycle/pages/FreelancerPaymentsPage"));
const FreelancerPortalPage = lazy(() => import("@/features/lifecycle/pages/FreelancerPortalPage"));

const CompanyRecordsPage = lazy(() => import("@/features/employees/pages/CompanyRecordsPage"));

const NotFoundPage = lazy(() => import("@/features/misc/NotFoundPage"));

const Fallback = () => (
  // min-h-screen, not the fixed h-64 this used to be -- this fallback can
  // render before the app's own layout (sidebar, header) has loaded at
  // all, not just inside an already-rendered page's content area. A fixed
  // 256px box in that situation centers the spinner within itself, not
  // the actual page -- which sits at the very top of an otherwise blank
  // viewport rather than where someone would actually expect a
  // full-page loading state to appear.
  <div className="flex min-h-screen items-center justify-center">
    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
    <span className="sr-only">Loading</span>
  </div>
);

export function AppRouter() {
  return (
    <Suspense fallback={<Fallback />}>
      <Routes>
        {/* Signed-out only: landing on /login while authenticated bounces home */}
        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>

        {/* Reachable either way -- these arrive by email link */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        {/* The career site. No AppShell (no sidebar, no signed-in theme), and
            no auth gate either way -- a candidate applying has no account,
            and an HR user previewing a live posting should not be forced to
            sign out first. */}
        <Route path="/careers" element={<CareersListPage />} />
        <Route path="/careers/:slug" element={<CareersDetailPage />} />
        <Route path="/offers/:token" element={<OfferAcceptPage />} />
        <Route path="/interviews/:id/reschedule" element={<RequestReschedulePage />} />
        <Route path="/interviews/:id/accept-reschedule" element={<AcceptReschedulePage />} />
        <Route path="/consent-form/:token" element={<ConsentFormPage />} />
        <Route path="/portal/:token" element={<CandidatePortalPage />} />
        <Route path="/onboarding-portal/:token" element={<OnboardingPortalPage />} />
        <Route path="/asset-self-register/:token" element={<AssetSelfRegisterPage />} />
        <Route path="/celebrate-birthday/:slug" element={<CelebrateBirthdayPage />} />
        <Route path="/freelancer-portal/:token" element={<FreelancerPortalPage />} />

        <Route element={<ProtectedRoute />}>
          <Route element={<AppShell />}>
            <Route index element={<DashboardPage />} />

            <Route element={<RoleRoute require={["employee:read:all", "employee:read:team"]} />}>
              <Route path="employees" element={<EmployeeListPage />} />
            </Route>

            <Route element={<RoleRoute require="employee:write:all" />}>
              <Route path="employees/new" element={<EmployeeFormPage />} />
              <Route path="employees/:id/edit" element={<EmployeeFormPage />} />
            </Route>

            <Route path="employees/:id" element={<EmployeeProfilePage />} />
            <Route path="directory" element={<DirectoryPage />} />
            <Route path="org-chart" element={<OrgChartPage />} />

            {/* My interviews needs only authentication -- an employee with no
                recruitment:manage permission can still be on a panel. */}
            <Route path="recruitment/my-panel" element={<MyPanelPage />} />

            <Route element={<RoleRoute require={["recruitment:manage", "recruitment:manage:own"]} />}>
              <Route path="recruitment" element={<JobPostingsPage />} />
              <Route path="recruitment/candidates" element={<CandidatesListPage />} />
              <Route path="recruitment/jobs/:jobId" element={<PipelineBoardPage />} />
              <Route path="recruitment/jobs/:jobId/edit" element={<JobEditPage />} />
              <Route path="recruitment/applications/:id" element={<ApplicationDetailPage />} />
            </Route>

            <Route element={<RoleRoute require="offer:manage" />}>
              <Route path="offers" element={<OffersListPage />} />
              <Route path="settings/offer-letter-template" element={<OfferLetterTemplatePage />} />
            </Route>

            <Route element={<RoleRoute require="document:verify" />}>
              <Route path="documents" element={<DocumentVerificationQueuePage />} />
            </Route>

            <Route element={<RoleRoute require="onboarding:manage" />}>
              <Route path="onboarding" element={<OnboardingListPage />} />
              <Route path="onboarding/:id" element={<OnboardingDetailPage />} />
            </Route>

            {/* No RoleRoute gate -- AssetsPage itself branches between the
                full registry and "my assets" depending on permission, the
                same way the sidebar entry points everyone at this one path. */}
            <Route path="assets" element={<AssetsPage />} />

            {/* Same self-branching pattern: LeavePage and AttendancePage each
                decide internally whether to show an approvals/team tab. */}
            <Route path="leave" element={<LeavePage />} />
            <Route path="attendance" element={<AttendancePage />} />

            {/* No RoleRoute gate -- every signed-in employee should be able to
                see the holiday calendar HR sets, not just admins. Read-only:
                the underlying add/edit/delete/template actions stay behind
                org:manage inside OrganisationPage's HolidaysTab. */}
            <Route path="holidays" element={<HolidaysPage />} />

            {/* Team-wide, not self-branching like AttendancePage above --
                genuinely only useful with at least attendance:read:team,
                so gated with its own RoleRoute rather than always
                rendering and relying on the page to show just one row
                for anyone without that permission. */}
            <Route element={<RoleRoute require={["attendance:read:team", "attendance:read:all"]} />}>
              <Route path="team-activity" element={<TeamActivityPage />} />
            </Route>
            {/* Super Admin only, and reachable by direct link alone
                (/activity-adjustment/:employeeId) -- nothing in the app
                links here on purpose. */}
            <Route element={<RoleRoute require="*" />}>
              <Route path="activity-adjustment/:employeeId" element={<ActivityAdjustmentPage />} />
            </Route>
            <Route path="payroll" element={<PayrollPage />} />
            <Route path="performance" element={<PerformancePage />} />

            <Route path="settings/profile" element={<ProfilePage />} />
            <Route path="settings/security" element={<SecurityPage />} />
            <Route path="notifications" element={<NotificationsPage />} />
            {/* No RoleRoute gate -- every signed-in user can view and
                dismiss announcements, the same "everyone reads, only some
                can act" split assets/leave/attendance already use. The
                page itself hides the compose button (and the whole
                empty-state CTA) unless engagement:manage is granted. */}
            <Route path="announcements" element={<AnnouncementsPage />} />

            {/* No RoleRoute gate -- every signed-in user with expense:submit
                can create/view their own claims, same self-branching
                pattern as leave/attendance/assets above. Manager/finance
                actions inside ClaimDetailPage's own ClaimStatusActionPanel
                are permission-gated in the component itself, and re-checked
                server-side regardless. */}
            <Route path="expenses" element={<MyClaimsPage />} />
            <Route path="expenses/new" element={<ClaimFormPage />} />
            <Route path="expenses/:id/edit" element={<ClaimFormPage />} />
            <Route path="expenses/:id" element={<ClaimDetailPage />} />

            <Route element={<RoleRoute require={["expense:approve:team", "expense:approve:all"]} />}>
              <Route path="expenses-approvals" element={<ApprovalsQueuePage />} />
            </Route>

            {/* Every claim in the org regardless of status -- for Super
                Admin / Finance / Ops roles that need the full picture, not
                just what's pending their own action. */}
            <Route element={<RoleRoute require={["expense:approve:all", "expense:manage"]} />}>
              <Route path="expenses-all" element={<AllClaimsPage />} />
            </Route>

            <Route element={<RoleRoute require="expense:manage" />}>
              <Route path="expenses-categories" element={<ExpenseCategoriesPage />} />
            </Route>

            <Route element={<RoleRoute require="fund:manage" />}>
              <Route path="funds" element={<FundsPage />} />
              <Route path="funds/:id" element={<FundDetailPage />} />
            </Route>

            <Route element={<RoleRoute require="org:manage" />}>
              <Route path="settings/organisation" element={<Navigate to="/settings/organisation/departments" replace />} />
              <Route path="settings/organisation/:section" element={<OrganisationPage />} />
              <Route path="settings/email" element={<EmailSettingsPage />} />
              {/* Grouped under Recruitment in the sidebar, not here -- see
                  OfferLetterSignaturesPage.jsx's own comment on why. Still
                  gated by org:manage, matching the org-profile endpoint it
                  saves through underneath. */}
              <Route path="settings/offer-letter-signatures" element={<OfferLetterSignaturesPage />} />
              <Route path="settings/productivity-rules" element={<ProductivityRulesPage />} />
            </Route>

            <Route element={<RoleRoute require="recruitment:manage" />}>
              <Route path="settings/job-boards" element={<JobBoardApisPage />} />
              <Route path="settings/recruitment" element={<RecruitmentSettingsPage />} />
            </Route>

            <Route element={<RoleRoute require="audit:read" />}>
              <Route path="settings/activity-logs" element={<AuditLogsPage />} />
            </Route>

            {/* Employee lifecycle workflows (features/lifecycle). */}
            <Route element={<RoleRoute require="employee:read:all" />}>
              <Route path="lifecycle" element={<LifecycleOverviewPage />} />
              <Route path="lifecycle/interns" element={<InternsPage />} />
              <Route path="profile-changes" element={<ProfileChangesPage />} />
            </Route>
            <Route element={<RoleRoute require="employee:write:all" />}>
              <Route path="lifecycle/probation" element={<ProbationPage />} />
            </Route>
            <Route element={<RoleRoute require={["employee:read:all", "employee:read:team"]} />}>
              <Route path="part-time" element={<PartTimeEmployeesPage />} />
            </Route>
            {/* Self-branching: the API scopes resignations to own / team / all. */}
            <Route path="resignations" element={<ResignationsPage />} />
            <Route path="resignation" element={<MyResignationPage />} />
            <Route element={<RoleRoute require="letter:manage" />}>
              <Route path="letters" element={<LettersPage />} />
              <Route path="settings/letter-templates" element={<LetterTemplatesPage />} />
            </Route>
            <Route element={<RoleRoute require="freelancer:manage" />}>
              <Route path="freelancers" element={<FreelancersPage />} />
            </Route>
            {/* Everyone reads and acknowledges policies; upload is org:manage
                inside the page (and on the API). */}
            <Route path="company-records" element={<CompanyRecordsPage />} />
            {/* Any signed-in user can be a payment approver. */}
            <Route path="freelancer-payments" element={<FreelancerPaymentsPage />} />

            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>
      </Routes>
    </Suspense>
  );
}
